import { Component, Input, Output, EventEmitter } from '@angular/core';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { 
  faPlus, faQuestionCircle, faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { SearchComponent, SearchFilters, SearchConfig } from '../search/search.component';
import { ButtonComponent } from '../button/button.component';

export interface ActionButton {
  type: 'add' | 'help' | 'custom';
  text?: string;
  icon?: any;
  visible?: boolean;
  disabled?: boolean;
  customClass?: string;
}

export interface HeaderActionsConfig {
  addButton?: ActionButton;
  helpButton?: ActionButton;
  customButtons?: ActionButton[];
  searchConfig?: SearchConfig;
  showAddButton?: boolean;
  showSearch?: boolean;
  showTagFilter?: boolean;
  showBackButton?: boolean;
  backButtonTitle?: string;
}

@Component({
  selector: 'app-header-actions',
  standalone: true,
  imports: [FontAwesomeModule, TranslateModule, SearchComponent, ButtonComponent],
  templateUrl: './header-actions.component.html',
  styleUrls: ['./header-actions.component.scss']
})
export class HeaderActionsComponent {
  @Input() config: HeaderActionsConfig = {
    addButton: {
      type: 'add',
      text: 'BUTTON.ADD.DEFAULT',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    helpButton: {
      type: 'help',
      icon: faQuestionCircle,
      visible: true,
      disabled: false
    },
    searchConfig: {
      showTextSearch: true,
      showTagFilter: true,
      textPlaceholder: 'SEARCH.PLACEHOLDER.DEFAULT',
      tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS'
    },
    showAddButton: true,
    showSearch: true,
    showTagFilter: true,
    showBackButton: false
  };

  @Input() initialFilters: SearchFilters = {
    searchTerm: '',
    selectedTag: ''
  };

  @Output() addClick = new EventEmitter<void>();
  @Output() helpClick = new EventEmitter<void>();
  @Output() backClick = new EventEmitter<void>();
  @Output() customButtonClick = new EventEmitter<{ type: string, index: number }>();

  @Output() filtersChanged = new EventEmitter<SearchFilters>();
  @Output() searchCleared = new EventEmitter<void>();
  @Output() tagFilterCleared = new EventEmitter<void>();
  @Output() allFiltersCleared = new EventEmitter<void>();

  @Input() headerIcon?: any; 
  @Input() headerTitle?: string;
  @Input() headerDescription?: string;
  @Input() showBackButtonOnly: boolean = false;

  @Input() count: number | string | null = null;
  @Input() showCount: boolean = false;

  faPlus = faPlus;
  faQuestionCircle = faQuestionCircle;
  faArrowLeft = faArrowLeft;

  constructor(private translate: TranslateService) {}

  onAddClick(): void {
    if (!this.config.addButton?.disabled) {
      this.addClick.emit();
    }
  }

  onHelpClick(): void {
    if (!this.config.helpButton?.disabled) {
      this.helpClick.emit();
    }
  }

  onBackClick(): void {
    this.backClick.emit();
  }

  onCustomButtonClick(button: ActionButton, index: number): void {
    if (!button.disabled) {
      this.customButtonClick.emit({ type: button.type, index });
    }
  }

  onSearchFiltersChanged(filters: SearchFilters): void {
    this.filtersChanged.emit(filters);
  }

  onSearchCleared(): void {
    this.searchCleared.emit();
  }

  onTagFilterCleared(): void {
    this.tagFilterCleared.emit();
  }

  onAllFiltersCleared(): void {
    this.allFiltersCleared.emit();
  }

  getAddButtonTitle(): string {
    const text = this.config.addButton?.text;
    return text ? this.translate.instant(text) : '';
  }

  getCustomButtonTitle(button: ActionButton): string {
    return button.text ? this.translate.instant(button.text) : '';
  }

  getAddButtonText(): string {
    const text = this.config.addButton?.text;
    return text ? this.translate.instant(text) : '';
  }

  getButtonText(button: ActionButton): string {
    return button.text ? this.translate.instant(button.text) : '';
  }

  getBackButtonTitle(): string {
    return this.config.backButtonTitle ? 
      this.translate.instant(this.config.backButtonTitle) : 
      this.translate.instant('BUTTON.BACK');
  }

  get showAddButton(): boolean {
    return this.config.showAddButton !== false && this.config.addButton?.visible !== false;
  }

  get showHelpButton(): boolean {
    return !!this.config.helpButton && this.config.helpButton.visible !== false;
  }

  get showBackButton(): boolean {
    return this.config.showBackButton === true;
  }

  get hasCustomButtons(): boolean {
    return !!(this.config.customButtons && this.config.customButtons.length > 0);
  }

  get showSearch(): boolean {
    return this.config.showSearch !== false && !!(this.config.searchConfig);
  }
}